/**
 * Shared add-to-cart feedback + guarded-add helpers (window.iwAtc).
 *
 * Framework-agnostic (vanilla DOM), so every ATC entry point can share one
 * implementation of the button state machine and the in-flight lock. Consumers
 * today: the PDP #add button (snippets/product_linked-options.liquid), the
 * sticky ATC (assets/product-sticky-atc.js) and the one-time button
 * (assets/one-time-atc.js). Loaded early in scripts_head so it is defined
 * before any inline PDP handler runs.
 *
 * A button adopts it as:
 *   iwAtc.guardedAdd(button, function () { return iwAtc.add(body, qty); }, line)
 *
 * API:
 *   iwAtc.add(body, qty) -> Promise
 *     The whole add, for guardedAdd's `run`: posts body (a FormData, or an
 *     object such as { id, quantity } sent as JSON) to /cart/add.js along with
 *     the drawer's sections (cart-drawer-data and cart-wallet-buttons), so the
 *     drawer and its wallet buttons redraw from the add's own response with
 *     no further requests. Reconciles a dropped connection
 *     against the cart (qty is the units being added), shows the failure
 *     message and rejects when the item wasn't added, and sends the shopper to
 *     /cart instead of the drawer when Mini_Cart is off.
 *
 *   iwAtc.setButtonState(button, 'adding' | 'added' | 'reset')
 *     Swaps the button label to Adding…/Added ✓ (spinner + success come from
 *     the .is-adding/.is-added CSS in custom.css), and toggles aria-busy /
 *     aria-disabled — never native `disabled`, so the button stays in the
 *     accessibility tree and screen readers announce the state change. The
 *     original markup (label span, price, arrow) is stored and restored on
 *     reset, so it works for buttons with or without an inner label element.
 *
 *   iwAtc.guardedAdd(button, run, line) -> Promise
 *     Per-button in-flight lock (a data-iw-adding flag on the element). No-ops
 *     if an add is already running on that button. Sets 'adding', awaits run(),
 *     then 'added' (brief flourish, auto-reset) on success or 'reset' on
 *     failure. Always releases the lock in a finally tied to run()'s real
 *     lifecycle. `run` owns all user-facing messaging; it must reject only when
 *     the item was NOT added (showAddedToCart swallows drawer failures inside
 *     run, so a drawer that didn't redraw still confirms the add). Also opens
 *     the cart drawer in a loading state once the add has run for
 *     window.iwCartDrawer.openDelayMs (Theme settings → Interaction timing),
 *     and closes it again if the add fails. `line` ({ variantId, sellingPlan },
 *     optional) names what is being added: a drawer line with that variant and
 *     plan shows the loading effect while it loads, and without a match — or
 *     without `line` — a skeleton row shows instead.
 *
 *   iwAtc.lineFromForm(form) -> { variantId, sellingPlan } | null
 *     The `line` for guardedAdd, read from a product form's id and
 *     selling_plan fields.
 *
 *   iwAtc.fetchJsonWithTimeout(url, options, ms) -> Promise<{response, data}>
 *     fetch() + JSON parse under one AbortController timeout (covers the body
 *     read, not just headers). A missing or non-positive ms falls back to 10s,
 *     so a call can never abort before it starts. Rejects on a non-2xx status
 *     or an unreadable body with an Error carrying `response` and `data` (null
 *     when unreadable), the way $.ajax rejects, so fetch and jQuery adds share
 *     one failure path.
 *
 *   iwAtc.addBackstopMs
 *     The time limit for /cart/add.js, on the fetch and jQuery paths alike.
 *     Abandoning an add only discards Shopify's answer — the add can still be
 *     applied — so the limit is long enough that only a connection that has
 *     stopped delivering reaches it, and reaching it runs the cart check
 *     (confirmAddLanded) rather than reporting a failure. It exists so the
 *     button always unlocks; "Still adding…" shows after 10s.
 *
 *   iwAtc.showFailureMessage(failure)
 *     Renders why an add failed into #cart-message, replacing the theme's
 *     showCartErrorMessage, which called every failure a stock-out — so a
 *     dropped connection told shoppers the product was gone. Takes the failure
 *     as thrown (a jqXHR or an Error, with or without `response`) and picks
 *     the wording from Theme settings → Cart via window.iwCartMessages.
 *
 *   iwAtc.shopifyAnswered(failure) -> boolean
 *     Whether Shopify gave a definite answer to the add (a 422, say), as
 *     opposed to one whose outcome is unknown and needs reconciling.
 *
 *   iwAtc.cartUnitsKnown() -> Promise<number|null>
 *     The cart's unit count before an add, for use as its baseline. Waits out
 *     a drawer redraw still in flight from the previous add, and reads the cart
 *     fresh when that redraw couldn't.
 *
 *   iwAtc.confirmAddLanded(failure, unitsBefore, qty) -> Promise<cart|null>
 *     Reconciles an add whose request never returned a status line against the
 *     real cart — read twice, 3s apart — so a dropped connection that still
 *     delivered the add is reported as the success it was. Callers hand the
 *     cart it read to the drawer refresh, where it serves as the rebuild's
 *     fallback copy.
 *
 *   iwAtc.readCart() -> Promise<cart|null>
 *     The cart as the server has it, or null when it can't be read.
 *
 *   iwAtc.resyncCartUi() -> Promise
 *     Redraws the drawer and progress bar from that cart, for the paths that
 *     end in a message rather than a confirmed add. Not awaited: a refused add
 *     created nothing to double-add, so the button needn't wait on a repaint to
 *     report the refusal.
 *
 *   iwAtc.showAddedToCart(cart, drawerSections) -> Promise
 *     The success message, the drawer opening and its redraw, for an add that
 *     landed. Redraws from drawerSections (the add's returned sections)
 *     when there are some, and otherwise reads the cart back, using `cart` as
 *     the fallback copy. Not awaited: the add is confirmed once Shopify accepts
 *     it, so the button reports that without holding its lock across a redraw.
 *     Ends the drawer's loading state once the redraw lands, and leaves the
 *     drawer closed if the shopper dismissed it while it loaded.
 */
(function () {
  if (window.iwAtc) return;

  var ADDING_LABEL = 'Adding…';
  var SLOW_LABEL = 'Still adding…';
  var ADDED_LABEL = 'Added ✓';
  var ADDED_HOLD_MS = 1800;
  var CART_READ_TIMEOUT_MS = 10000;
  var RECOVERY_READS = 2;
  var RECOVERY_RECHECK_MS = 3000;
  var SLOW_NOTICE_MS = 10000;
  var MESSAGE_HOLD_MS = 5000;
  var OUTCOME_UNKNOWN_STATUSES = [502, 503, 504];
  var addBackstopMs = 150000;
  var drawerSectionIds =
    typeof cartDrawerSections === 'string' ? cartDrawerSections : 'cart-drawer-data';

  // Used when the theme settings haven't rendered into the page, so a failed
  // add always says something.
  var FALLBACK_MESSAGES = {
    network: "We couldn't reach the cart. Check your connection and try again.",
    unavailable: 'Sorry! Seems like the product is out of stock',
    generic: 'Something went wrong adding this to your cart. Please try again.',
  };

  var ADDING_ANNOUNCE = 'Adding to cart';
  var SLOW_ANNOUNCE = 'Still adding to cart';
  var ADDED_ANNOUNCE = 'Added to cart';
  var ERROR_ANNOUNCE = 'Unable to add to cart';

  var pendingRedraw = Promise.resolve();
  var cartObjectStale = false;
  var drawerLoadInFlight = null;
  var activeDrawerLoads = 0;

  var liveRegion = null;
  var announceTimer = null;
  function ensureLiveRegion() {
    if (liveRegion) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.cssText =
      'position:absolute;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);' +
      'white-space:nowrap;border:0;';
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  function announce(message) {
    if (!message) return;
    var region = ensureLiveRegion();
    clearTimeout(announceTimer);
    region.textContent = '';
    announceTimer = setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  // Normalizes the shapes a failed add arrives in — a jqXHR, or an Error that
  // may carry the fetch `response` — down to an HTTP status, or 0 when the
  // add's outcome is unknown. That covers a transport failure that never
  // reached a status line (offline, reset, DNS, the add backstop), a 2xx whose
  // body couldn't be read (the add was accepted; only the answer was lost), and
  // a gateway error, which Shopify's edge can return after the add was applied.
  function failureStatus(failure) {
    var status = 0;
    if (failure && failure.response) status = failure.response.status || 0;
    else if (failure && typeof failure.status === 'number') status = failure.status;
    var outcomeUnknown =
      (status >= 200 && status < 300) || OUTCOME_UNKNOWN_STATUSES.indexOf(status) !== -1;
    return outcomeUnknown ? 0 : status;
  }

  function shopifyAnswered(failure) {
    return failureStatus(failure) !== 0;
  }

  // Shopify's own wording for a rejected item ("You can only add 1 of X to your
  // cart"), which is always more specific than the configured fallback.
  function shopifyReason(failure) {
    var body = failure && (failure.data || failure.responseJSON);
    return (body && body.description) || '';
  }

  var messageTimer = null;

  function showFailureMessage(failure) {
    var status = failureStatus(failure);
    var configured = window.iwCartMessages || {};
    var text;
    if (status === 0) {
      text = configured.network || FALLBACK_MESSAGES.network;
    } else if (status === 422) {
      text =
        shopifyReason(failure) ||
        configured.unavailable ||
        FALLBACK_MESSAGES.unavailable;
    } else {
      text = configured.generic || FALLBACK_MESSAGES.generic;
    }

    var el = document.getElementById('cart-message');
    if (!el) return;
    el.classList.remove('message-success');
    el.classList.add('message-error');
    el.textContent = text;
    el.style.display = 'block';
    clearTimeout(messageTimer);
    messageTimer = setTimeout(function () {
      el.style.display = 'none';
    }, MESSAGE_HOLD_MS);
  }

  function labelMarkup(text) {
    var span = document.createElement('span');
    span.className = 'iw-atc-label';
    span.textContent = text;
    return span.outerHTML;
  }

  // Swaps only the visible label, leaving the stored original markup and the
  // button's state classes alone, so it is safe to call mid-add.
  function setLabel(button, text) {
    if (!button) return;
    button.innerHTML = labelMarkup(text);
  }

  function setButtonState(button, state) {
    if (!button) return;
    if (state === 'adding') {
      if (button.dataset.iwLabel == null) button.dataset.iwLabel = button.innerHTML;
      button.classList.add('is-adding');
      button.classList.remove('is-added');
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      setLabel(button, ADDING_LABEL);
    } else if (state === 'added') {
      button.classList.remove('is-adding');
      button.classList.add('is-added');
      button.setAttribute('aria-busy', 'false');
      button.removeAttribute('aria-disabled');
      setLabel(button, ADDED_LABEL);
    } else {
      button.classList.remove('is-adding');
      button.classList.remove('is-added');
      button.setAttribute('aria-busy', 'false');
      button.removeAttribute('aria-disabled');
      if (button.dataset.iwLabel != null) {
        button.innerHTML = button.dataset.iwLabel;
        delete button.dataset.iwLabel;
      }
    }
  }

  function miniCartEnabled() {
    return typeof getglobalLib !== 'function' || getglobalLib('Mini_Cart') === 'yes';
  }

  function drawerOpenDelayMs() {
    return window.iwCartDrawer.openDelayMs;
  }

  // Opens the drawer shortly after an add starts rather than after Shopify
  // answers, with the line being added shown loading until the redraw lands.
  // aria-busy rather than a class, because iw-header-a11y.js re-runs the
  // drawer's focus handling on every class change. Null when there is no
  // drawer to open: the cart page, or Mini_Cart turned off.
  function beginDrawerLoad(line) {
    var drawer = document.getElementById('CartSidebar');
    if (!drawer || !miniCartEnabled()) return null;
    var load = { drawer: drawer, opened: false, claimed: false, ended: false, timer: null, row: null, rowIsSkeleton: false };
    activeDrawerLoads++;
    drawer.setAttribute('aria-busy', 'true');
    markLoadingRow(load, line);
    load.timer = setTimeout(function () {
      openDrawerFor(load);
    }, drawerOpenDelayMs());
    return load;
  }

  // The add lands on the existing line with the same variant and selling plan,
  // so that line shows the loading effect; anything else becomes a new line,
  // so a skeleton row stands in for it unless Theme settings turn the skeleton
  // off. The redraw replaces both with the real lines.
  function markLoadingRow(load, line) {
    var list = load.drawer.querySelector('.js__ajax-products-bind');
    if (!list) return;
    var existing = line ? findCartLine(list, line) : null;
    if (existing) {
      existing.setAttribute('aria-busy', 'true');
      load.row = existing;
      return;
    }
    if (window.iwCartDrawer && window.iwCartDrawer.skeleton === false) return;
    var skeleton = document.createElement('li');
    skeleton.className = 'cart-line-skeleton';
    skeleton.setAttribute('aria-busy', 'true');
    skeleton.setAttribute('aria-hidden', 'true');
    skeleton.innerHTML =
      '<span class="cart-line-skeleton__image"></span>' +
      '<span class="cart-line-skeleton__text"><span></span><span></span><span></span></span>';
    list.prepend(skeleton);
    load.row = skeleton;
    load.rowIsSkeleton = true;
  }

  function findCartLine(list, line) {
    var rows = list.querySelectorAll('.js__cart-table-item-row');
    for (var i = 0; i < rows.length; i++) {
      if (
        String(rows[i].dataset.variantId) === String(line.variantId) &&
        String(rows[i].dataset.sellingPlan || '') === String(line.sellingPlan || '')
      ) {
        return rows[i];
      }
    }
    return null;
  }

  function lineFromForm(form) {
    if (!form) return null;
    var data = new FormData(form);
    return { variantId: data.get('id'), sellingPlan: data.get('selling_plan') || '' };
  }

  function clearLoadingRow(load) {
    var row = load.row;
    load.row = null;
    if (!row || !row.isConnected) return;
    if (load.rowIsSkeleton) row.remove();
    else row.removeAttribute('aria-busy');
  }

  function openDrawerFor(load) {
    if (load.opened) return;
    load.opened = true;
    if (typeof openMiniCart === 'function') openMiniCart();
  }

  // The shopper closed the drawer while it was still loading, so the finished
  // add shouldn't throw it back open.
  function drawerDismissed(load) {
    return !!load && load.opened && !load.drawer.classList.contains('active');
  }

  function endDrawerLoad(load) {
    if (!load || load.ended) return;
    load.ended = true;
    clearTimeout(load.timer);
    clearLoadingRow(load);
    activeDrawerLoads--;
    if (activeDrawerLoads === 0) load.drawer.removeAttribute('aria-busy');
  }

  // For an add that failed: a drawer opened only to show it loading closes
  // again, so the failure message beneath it is seen.
  function abandonDrawerLoad(load) {
    if (!load || load.ended) return;
    endDrawerLoad(load);
    if (!load.opened) return;
    var closeButton = document.querySelector('#CartSidebar.active .js__cart-close');
    if (closeButton) closeButton.click();
  }

  var resetTimers = new WeakMap();

  function guardedAdd(button, run, line) {
    if (button && button.dataset.iwAdding) return Promise.resolve();
    var drawerLoad = beginDrawerLoad(line);
    drawerLoadInFlight = drawerLoad;
    if (button) {
      button.dataset.iwAdding = '1';
      if (resetTimers.has(button)) {
        clearTimeout(resetTimers.get(button));
        resetTimers.delete(button);
      }
    }
    setButtonState(button, 'adding');
    announce(ADDING_ANNOUNCE);
    var slowTimer = setTimeout(function () {
      if (button && button.dataset.iwAdding) {
        setLabel(button, SLOW_LABEL);
        announce(SLOW_ANNOUNCE);
      }
    }, SLOW_NOTICE_MS);
    return Promise.resolve()
      .then(run)
      .then(
        function () {
          if (drawerLoad && !drawerLoad.claimed) endDrawerLoad(drawerLoad);
          setButtonState(button, 'added');
          announce(ADDED_ANNOUNCE);
          if (button) {
            resetTimers.set(
              button,
              setTimeout(function () {
                resetTimers.delete(button);
                setButtonState(button, 'reset');
              }, ADDED_HOLD_MS)
            );
          }
        },
        function (err) {
          console.warn('ATC add failed', err);
          if (drawerLoad && !drawerLoad.claimed) abandonDrawerLoad(drawerLoad);
          setButtonState(button, 'reset');
          announce(ERROR_ANNOUNCE);
        }
      )
      .finally(function () {
        clearTimeout(slowTimer);
        if (drawerLoadInFlight === drawerLoad) drawerLoadInFlight = null;
        if (button) delete button.dataset.iwAdding;
      });
  }

  async function fetchJson(url, options) {
    var response = await fetch(url, options || {});
    var data = null;
    try {
      data = await response.json();
    } catch (parseErr) {
      if (parseErr && parseErr.name === 'AbortError') throw parseErr;
    }
    if (!response.ok || data === null) {
      var failure = new Error(
        url + ' answered ' + response.status + (data === null ? ' with an unreadable body' : '')
      );
      failure.response = response;
      failure.data = data;
      throw failure;
    }
    return { response: response, data: data };
  }

  async function fetchJsonWithTimeout(url, options, ms) {
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, ms > 0 ? ms : CART_READ_TIMEOUT_MS);
    try {
      return await fetchJson(url, Object.assign({}, options, { signal: controller.signal }));
    } finally {
      clearTimeout(timer);
    }
  }

  // The cart's unit count before an add. _sm-theme's quickCartTotal keeps the
  // global cartObject in step with the drawer on every redraw — after an add, a
  // drawer quantity change, a removal — and renders that same item_count into
  // the cart count, so the baseline can be read as data rather than parsed back
  // out of markup. The rendered count stands in only for a page where that
  // global never initialized. A redraw still running from the previous add
  // hasn't counted it yet, so this waits it out; one that couldn't read the
  // cart left cartObject behind the server, so the count is read fresh instead.
  // Null when no trustworthy count is available.
  async function cartUnitsKnown() {
    await pendingRedraw;
    if (cartObjectStale) {
      var cart = await readCart();
      return cart ? cart.item_count : null;
    }
    var known = window.cartObject && window.cartObject.item_count;
    if (typeof known === 'number') return known;
    var el = document.querySelector('.js__ajax-cart-count');
    var count = el ? parseInt(el.textContent, 10) : NaN;
    return isNaN(count) ? null : count;
  }

  // An add that ends without a status line is unresolved rather than refused:
  // a dropped connection can lose Shopify's answer after the add was applied,
  // and an add still in transit can land after a first read of the cart. So the
  // cart is read, and read once more after a pause, before an add is called
  // failed. Confirmation needs the cart to have grown by the whole quantity
  // over the baseline; with no baseline there is nothing to confirm against.
  // Resolves to null when the add can't be shown to have landed, or when
  // Shopify answered with a status of its own and there is nothing to reconcile.
  async function confirmAddLanded(failure, unitsBefore, qty) {
    if (failureStatus(failure) !== 0 || typeof unitsBefore !== 'number') return null;
    var unitsWanted = unitsBefore + (qty > 0 ? qty : 1);
    for (var attempt = 0; attempt < RECOVERY_READS; attempt++) {
      if (attempt > 0) await wait(RECOVERY_RECHECK_MS);
      var cart = await readCart();
      if (cart && cart.item_count >= unitsWanted) return cart;
    }
    return null;
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // Reads the cart as the server has it, resolving to null rather than throwing
  // so callers can treat an unreadable cart as nothing to reconcile and leave
  // the UI as it stands.
  async function readCart() {
    try {
      return (await fetchJsonWithTimeout('/cart.js', {}, CART_READ_TIMEOUT_MS)).data;
    } catch (readErr) {
      return null;
    }
  }

  // Pulls the drawer and progress bar back in line with the server's cart after
  // an add Shopify declined, so a quantity or count that had drifted out of step
  // is corrected alongside the message. Mirrors the success path without its
  // confirmation.
  async function resyncCartUi() {
    try {
      if (typeof getglobalLib === 'function' && getglobalLib('Mini_Cart') !== 'yes') return;
      var cart = await readCart();
      if (!cart) return;
      if (typeof reloadAjaxCartItemUsingCartAjaxObject === 'function') {
        await reloadAjaxCartItemUsingCartAjaxObject(cart);
      }
    } catch (resyncErr) {
      console.warn('cart resync failed', resyncErr);
    }
  }

  // Confirms a landed add to the shopper: the success message, the drawer
  // opening and its redraw. The redraw uses the add's returned sections
  // when they came back, and otherwise re-reads the cart, with the cart passed
  // here (handed over by an add that recovered against one) as its fallback
  // copy. The redraw is recorded as pending so the next add's baseline waits
  // for it. Swallows its own failures, since a drawer that never redrew
  // doesn't mean the add didn't happen.
  function showAddedToCart(cart, drawerSections) {
    var load = drawerLoadInFlight;
    drawerLoadInFlight = null;
    if (load) load.claimed = true;
    pendingRedraw = redrawAfterAdd(cart, load, drawerSections).catch(function (redrawErr) {
      console.warn('cart redraw failed', redrawErr);
    });
    return pendingRedraw;
  }

  // quickCartTotal replaces cartObject whenever the rebuild renders a real
  // cart, so a cartObject that is still the same object afterwards means the
  // rebuild fell back to CartJS's copy, which never saw this add.
  async function redrawAfterAdd(cart, load, drawerSections) {
    var cartObjectBefore = window.cartObject;
    try {
      if (load) clearTimeout(load.timer);
      if (!drawerDismissed(load)) {
        if (load) load.opened = true;
        if (typeof showCartSuccessMessage === 'function') showCartSuccessMessage();
      }
      var drawn =
        !!drawerSections &&
        typeof renderCartDrawerSection === 'function' &&
        renderCartDrawerSection(drawerSections);
      if (!drawn && typeof reloadAjaxCartItemUsingCartAjaxObject === 'function') {
        await reloadAjaxCartItemUsingCartAjaxObject(cart);
      }
    } catch (drawerErr) {
      console.warn('cart drawer refresh failed (item was added)', drawerErr);
    }
    endDrawerLoad(load);
    cartObjectStale = window.cartObject === cartObjectBefore;
  }

  function addRequest(body) {
    var headers = { 'X-Requested-With': 'XMLHttpRequest' };
    if (body instanceof FormData) {
      body.append('sections', drawerSectionIds);
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(Object.assign({}, body, { sections: drawerSectionIds }));
    }
    return { method: 'POST', headers: headers, body: body };
  }

  async function add(body, qty) {
    var unitsBefore = await cartUnitsKnown();
    var confirmedCart = null;
    var drawerSections = null;
    try {
      var result = await fetchJsonWithTimeout('/cart/add.js', addRequest(body), addBackstopMs);
      drawerSections = result.data.sections || null;
    } catch (addErr) {
      confirmedCart = await confirmAddLanded(addErr, unitsBefore, qty);
      if (!confirmedCart) {
        showFailureMessage(addErr);
        if (shopifyAnswered(addErr)) resyncCartUi();
        throw addErr;
      }
    }
    if (!miniCartEnabled()) {
      window.location.href = '/cart';
      return;
    }
    showAddedToCart(confirmedCart, drawerSections);
  }

  window.iwAtc = {
    add: add,
    setButtonState: setButtonState,
    guardedAdd: guardedAdd,
    lineFromForm: lineFromForm,
    fetchJsonWithTimeout: fetchJsonWithTimeout,
    addBackstopMs: addBackstopMs,
    showFailureMessage: showFailureMessage,
    shopifyAnswered: shopifyAnswered,
    cartUnitsKnown: cartUnitsKnown,
    confirmAddLanded: confirmAddLanded,
    readCart: readCart,
    resyncCartUi: resyncCartUi,
    showAddedToCart: showAddedToCart,
    announce: announce,
  };
})();
